import React, { useLayoutEffect, useRef } from "react";
import { Link } from "react-router-dom";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import ServicePageShell, {
  FaqList,
  RelatedServices,
  SectionWrap,
  buildServiceSchemas,
} from "../../components/ServicePageShell";
import ServiceInquiryForm from "../../components/ServiceInquiryForm";
import hero from "../../assets/img/Q.webp";
import saasErpHero from "../../assets/img/saas-erp-development-hero.png";
import saasVsErpImg from "../../assets/img/saas-vs-erp.png";
import whoItIsForImg from "../../assets/img/who-it-is-for.png";
import coreFeaturesImg from "../../assets/img/core-features.png";
import buildVsBuyImg from "../../assets/img/build-vs-buy.png";
import developmentFlowImg from "../../assets/img/development-flow.png";
import saasErpFaqImg from "../../assets/img/saas-erp-developmentfaq.png";

gsap.registerPlugin(ScrollTrigger);

const pagePath = "/services/saas-erp-development";

const faqs = [
  {
    question: "What is the difference between SaaS and ERP?",
    answer:
      "SaaS is software delivered to many customers through a subscription model. ERP is the operational system a company uses internally to manage work across departments. We build both when the business model needs them.",
  },
  {
    question: "Can you build a multi-tenant SaaS platform?",
    answer:
      "Yes. We design multi-tenant architecture, role-based permissions, and data separation so the platform can serve multiple customers safely and efficiently.",
  },
  {
    question: "Do you integrate ERP with existing tools?",
    answer:
      "Absolutely. We connect ERP systems with accounting tools, CRMs, warehouses, payment gateways, APIs, and other software that the business already depends on.",
  },
  {
    question: "Which industries benefit most from custom ERP development?",
    answer:
      "Manufacturing, retail, healthcare, logistics, and other process-heavy businesses usually see strong returns because they need more control than off-the-shelf tools provide.",
  },
  {
    question: "How do you decide whether to build or buy?",
    answer:
      "We look at process complexity, integration needs, ownership, scalability, and cost over time. If your operations are unique, custom development often delivers the stronger long-term fit.",
  },
];

const schemas = buildServiceSchemas({
  name: "SaaS & ERP Development",
  description:
    "QBITS9 is a custom SaaS and ERP development company India, building secure multi-tenant platforms, role-based access, dashboards, and integrations.",
  path: pagePath,
  serviceType: "custom SaaS and ERP development company India",
  faqs,
});

const heroStats = [
  {
    title: "Multi-tenant ready",
    value: "Built to serve multiple customers or branches securely",
  },
  {
    title: "Operations focused",
    value: "Designed around approvals, reporting, and control",
  },
  {
    title: "Integration first",
    value: "Connects cleanly with the rest of your stack",
  },
];

const industries = [
  {
    badge: "M",
    title: "Manufacturing",
    description:
      "Track production, inventory, procurement, quality, and dispatch in a unified system built around the plant workflow.",
  },
  {
    badge: "R",
    title: "Retail",
    description:
      "Manage stores, catalogues, stock movement, billing, and reporting across locations and channels.",
  },
  {
    badge: "H",
    title: "Healthcare",
    description:
      "Organize appointments, records, access controls, and operational visibility with secure, role-based workflows.",
  },
  {
    badge: "L",
    title: "Logistics",
    description:
      "Centralize shipment tracking, vendor coordination, fleet visibility, and service metrics in one platform.",
  },
];

const features = [
  {
    badge: "MT",
    title: "Multi-tenant architecture",
    description:
      "Structure the system so different customers, brands, or business units can operate independently inside one product.",
  },
  {
    badge: "RB",
    title: "Role-based access",
    description:
      "Control who can see, approve, edit, or export data with clear permissions and audit-friendly workflows.",
  },
  {
    badge: "RD",
    title: "Reporting dashboards",
    description:
      "Turn operational data into practical dashboards for leadership, managers, and frontline users.",
  },
  {
    badge: "IN",
    title: "Integrations",
    description:
      "Connect CRM, accounting, eCommerce, payment, shipping, and data tools so the platform becomes the operating layer.",
  },
];

const saasVsErpCards = [
  {
    badge: "S",
    title: "SaaS",
    text: "A subscription product delivered to many customers. The focus is onboarding, user management, billing, permissions, feature delivery, and scale.",
  },
  {
    badge: "E",
    title: "ERP",
    text: "An internal operating system for one business. The focus is approvals, data visibility, process control, and better coordination across departments.",
  },
];

const buildVsBuy = [
  {
    title: "Build",
    points: [
      "Matches your exact workflow and business model.",
      "Lets you own the product direction and data structure.",
      "Scales without being boxed in by fixed software limits.",
      "Creates a product asset that can be differentiated over time.",
    ],
  },
  {
    title: "Buy",
    points: [
      "Useful when the process is standard and simple.",
      "Can be faster at the start but often needs workarounds.",
      "May lead to disconnected systems and extra licenses.",
      "Usually limits how deeply you can tailor the experience.",
    ],
  },
];

const processSteps = [
  {
    label: "Discovery",
    title: "Map the operating model",
    description:
      "We understand the business process, users, data, and reporting needs before any architecture is locked in.",
  },
  {
    label: "Design",
    title: "Plan the system structure",
    description:
      "We define architecture, permissions, user flows, and integration points for the product or ERP.",
  },
  {
    label: "Build",
    title: "Develop the platform",
    description:
      "We implement the front end, back end, dashboards, and workflows in a maintainable codebase.",
  },
  {
    label: "Integrate",
    title: "Connect the ecosystem",
    description:
      "We link the platform with the tools, APIs, and data sources that keep the business moving.",
  },
  {
    label: "Launch",
    title: "Deploy and optimize",
    description:
      "We roll out the system in phases and tune it once real users start working in it.",
  },
];

const relatedLinks = [
  {
    to: "/",
    kicker: "Home",
    title: "Back to QBITS9",
    description: "Return to the homepage and review the company story, services, and client work.",
    cta: "Open home",
  },
  {
    to: "/services/ai-automation",
    kicker: "Automation",
    title: "AI automation services",
    description: "Reduce manual work and connect your operations with AI-driven workflows.",
    cta: "View service",
  },
  {
    to: "/services/web-mobile-development",
    kicker: "Product work",
    title: "Web & mobile apps",
    description: "Build web apps, mobile apps, PWA experiences, and the APIs that connect them.",
    cta: "View service",
  },
  {
    to: "/services/desktop-software",
    kicker: "Desktop tools",
    title: "Desktop software",
    description: "Create desktop tools that connect with ERP workflows, reports, and operations.",
    cta: "View service",
  },
  {
    to: "/contact",
    kicker: "CTA",
    title: "Book a consultation",
    description: "Tell us about your SaaS or ERP idea and start a focused discovery conversation.",
    cta: "Contact us",
  },
];

const SaasErpDevelopment = () => {
  const textSectionRef = useRef(null);
  const textRef = useRef(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const mm = gsap.matchMedia();

      const setupSplitAnimation = ({ pin, start, end }) => {
        const textElement = textRef.current;
        const sectionElement = textSectionRef.current;
        if (!textElement || !sectionElement) return undefined;

        const textContent = textElement.textContent;
        textElement.innerHTML = "";

        textContent.split("").forEach((char) => {
          const span = document.createElement("span");
          span.textContent = char;
          span.style.color = "#8C8C8C";
          textElement.appendChild(span);
        });

        const chars = textElement.querySelectorAll("span");

        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: sectionElement,
            start,
            end,
            pin,
            scrub: 2,
            invalidateOnRefresh: true,
          },
        });

        tl.to(chars, {
          color: "#000000",
          stagger: 0.1,
          ease: "none",
        });

        return () => {
          tl.kill();
          textElement.textContent = textContent;
        };
      };

      mm.add("(min-width: 768px)", () =>
        setupSplitAnimation({
          pin: true,
          start: "top top",
          end: "+=150%",
        })
      );

      mm.add("(max-width: 767px)", () =>
        setupSplitAnimation({
          pin: true,
          start: "top top",
          end: "+=120%",
        })
      );
    });

    return () => ctx.revert();
  }, []);

  return (
    <ServicePageShell
      title="SaaS & ERP Development | QBITS9"
      description="QBITS9 is a custom SaaS and ERP development company India, building secure platforms with multi-tenant architecture, role-based access, dashboards, and integrations."
      canonicalPath={pagePath}
      keywords="custom SaaS and ERP development company India, SaaS development India, ERP development India, multi-tenant architecture, role-based access, reporting dashboards"
      schemas={schemas}
    >
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 top-[4%] md:w-[350px] w-[280px]">
          <img
            src={hero}
            alt="SaaS and ERP development services illustration"
            className="w-full h-auto aspect-auto"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            width="800"
            height="800"
          />
        </div>
        <div className="mx-auto max-w-7xl px-5 py-16 md:py-24 lg:px-16">
          <nav className="flex flex-wrap items-center gap-2 lg:text-base text-sm font-[500] text-blue font-poppins lg:w-[65%] w-full px-5 lg:px-0">
            <Link to="/" className="hover:text-[#FF6B00]">
              Home
            </Link>
            <span>/</span>
            <span>Services</span>
            <span>/</span>
            <span className="text-slate-900">SaaS &amp; ERP Development</span>
          </nav>

          <div className="mt-8 grid gap-10 lg:grid-cols-[minmax(0,1.1fr)_minmax(320px,0.9fr)] lg:items-center">
            <div className="space-y-8">
              <div className="space-y-5">
                <span className="font-peachi text-[16px] md:text-[18px] text-black font-[500] text-black mb-3">
                  Platform architecture for operations-driven businesses
                </span>
                <h1 className="text-black xl:text-[58px] lg:text-[50px] md:text-[42px] text-[28px] font-bold leading-[1.3]">
                  Custom SaaS and ERP development company India for platforms built around your business.
                </h1>
                <p className="max-w-2xl text-lg leading-8 text-slate-600 font-poppins">
                  QBITS9 builds secure multi-tenant SaaS platforms and ERP systems that centralize operations, automate approvals, and give leadership real-time visibility.
                </p>
              </div>

              <div className="flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  className="w-fit relative overflow-hidden flex items-center justify-center bg-primary px-6 sm:px-8 lg:px-[29px] py-3 lg:py-[15px] text-sm lg:text-base rounded-full group cursor-pointer"
                >
                  <span className="relative z-10 text-white font-unbounded text-sm lg:text-base whitespace-nowrap">Book a Free Consultation</span>
                  <span className="absolute inset-1/2 h-0 w-0 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#E85D00] transition-all duration-500 group-hover:h-[25rem] group-hover:w-[25rem]" />
                </Link>
                <Link
                  to="/services/ai-automation"
                  className="w-fit relative overflow-hidden flex items-center justify-center bg-blue px-6 sm:px-8 lg:px-[29px] py-3 lg:py-[15px] text-sm lg:text-base rounded-full group"
                >
                  <span className="relative z-10 text-white font-unbounded text-sm lg:text-base whitespace-nowrap">See AI Automation</span>
                  <span className="absolute inset-1/2 w-0 h-0 bg-secondary rounded-full transition-all duration-700 group-hover:w-[25rem] group-hover:h-[25rem] -translate-x-1/2 -translate-y-1/2" />
                </Link>
              </div>

              <div className="grid gap-4 sm:grid-cols-3">
                {heroStats.map((stat) => (
                  <article
                    key={stat.title}
                    className="h-full bg-white rounded-[22px] py-[18px] px-[18px] flex flex-col gap-2 shadow-[0_12px_30px_0_#0000000F] hover:shadow-[0_12px_30px_0_#00000030] transition duration-500 ease-in-out cursor-pointer"
                  >
                    <p className="text-[18px] font-bold text-primary">
                      {stat.title}
                    </p>
                    <p className="text-[15px] font-light text-lightBlack line-clamp-3">
                      {stat.value}
                    </p>
                  </article>
                ))}
              </div>
            </div>

            <div className="lg:pl-4">
              <div className="">
                <img
                  className="rounded-[40px]"
                  src={saasErpHero}
                  alt="Architecture diagram for a SaaS platform and ERP system with secure modules and dashboard layers."
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- TEXT SPLIT PIN SECTION ---------- */}
      <section
        ref={textSectionRef}
        className="text_animation min-h-screen lg:mt-[-150px] mt-0 flex items-center justify-center px-5 lg:py-0 py-[80px] w-full overflow-hidden"
      >
        <div
          aria-hidden="true"
          ref={textRef}
          className="font-peachi text-[#8C8C8C] text-[22px] md:text-[30px] lg:text-[38px] leading-[1.7] max-w-6xl text-center"
        >
          SaaS and ERP solve different business problems, but both can become strategic assets when they are built to fit the company instead of forcing the company to adapt.
        </div>
        <p className="sr-only">
          SaaS and ERP solve different business problems, but both can become strategic assets when they are built to fit the company instead of forcing the company to adapt.
        </p>
      </section>

      <section className="pb-16 md:pb-20 relative">
        <div className="mx-auto max-w-7xl px-5 lg:px-16">
          <div className="grid gap-10 lg:grid-cols-[minmax(0,1.1fr)_minmax(360px,0.9fr)] lg:items-center">
            <div className="space-y-8">
              <div className="space-y-3">
                <h3 data-aos="fade-up" className="font-peachi text-[28px] font-bold text-black mb-4 lg:mb-8 aos-init aos-animate">
                  <span className="text-primary"> SaaS vs ERP </span>
                </h3>
                <h2 className="text-3xl font-semibold leading-tight text-slate-900 md:text-4xl font-unbounded">
                  A brief guide to the difference and when each one matters
                </h2>
                <p className="text-sm leading-7 text-slate-600 font-poppins">
                  SaaS and ERP solve different business problems, but both can become strategic assets when they are built to fit the company instead of forcing the company to adapt.
                </p>
              </div>
              <div className="grid gap-3 md:grid-cols-1 xl:grid-cols-2">
                {saasVsErpCards.map((item) => (
                  <article
                    key={item.title}
                    className="group flex h-full flex-col justify-between rounded-[20px] border border-slate-200 bg-white p-4 shadow-[0_16px_45px_rgba(0,0,0,0.08)] transition duration-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(0,0,0,0.12)]"
                  >
                    <div className="space-y-4">
                      {/* <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#FFF3E9] text-lg font-semibold text-[#FF6B00]">
                        {item.badge}
                      </span> */}
                      <h3 className="text-[18px] font-semibold text-slate-900 font-unbounded leading-tight">
                        {item.title}
                      </h3>
                      <p className="text-sm leading-7 text-slate-600 font-poppins">
                        {item.text}
                      </p>
                    </div>
                  </article>
                ))}
              </div>
            </div>

            <div className="relative">
              <img
                src={saasVsErpImg}
                alt="Comparison visual for SaaS and ERP systems"
                className="w-full h-auto rounded-[32px]"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="pb-16 md:pb-20 relative">
        <div className="mx-auto max-w-7xl px-5 lg:px-16">
          <div className="grid gap-10 lg:grid-cols-[minmax(0,1.1fr)_minmax(360px,0.9fr)] lg:items-center">
            <div className="relative">
              <img
                src={whoItIsForImg}
                alt="Who it is for illustration"
                className="w-full h-auto rounded-[32px]"
                loading="lazy"
                decoding="async"
              />
            </div>

            <div className="space-y-8">
              <div className="space-y-3">
                <h3 data-aos="fade-up" className="font-peachi text-[28px] font-bold text-black mb-2 lg:mb-8 aos-init aos-animate">
                  <span className="text-primary"> Who it is for </span>
                </h3>
                <h2 className="text-3xl font-semibold leading-tight text-slate-900 md:text-4xl font-unbounded">
                  Industries that gain the most from custom platform work
                </h2>
                <p className="text-sm leading-7 text-slate-600 font-poppins">
                  When operations are complex and the business relies on visibility, a custom system often gives the clearest return.
                </p>
              </div>
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-2">
                {industries.map((item) => (
                  <article
                    key={item.title}
                    className="group flex h-full flex-col justify-between rounded-[20px] border border-slate-200 bg-white p-4 shadow-[0_16px_45px_rgba(0,0,0,0.08)] transition duration-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(0,0,0,0.12)]"
                  >
                    <div className="space-y-2">
                      <h3 className="text-[18px] font-semibold text-slate-900 font-unbounded leading-tight">
                        {item.title}
                      </h3>
                      <p className="text-sm leading-7 text-slate-600 font-poppins">
                        {item.description}
                      </p>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-16 md:pb-20 relative">
        <div className="mx-auto max-w-7xl px-5 lg:px-16">
          <div className="grid gap-10 lg:grid-cols-[minmax(0,1.1fr)_minmax(360px,0.9fr)] lg:items-center">
            <div className="space-y-8">
              <div className="space-y-3">
                <h3 data-aos="fade-up" className="font-peachi text-[28px] font-bold text-black mb-4 lg:mb-8 aos-init aos-animate">
                  <span className="text-primary"> Core features </span>
                </h3>
                <h2 className="text-3xl font-semibold leading-tight text-slate-900 md:text-4xl font-unbounded">
                  Features we commonly build into SaaS and ERP systems
                </h2>
                <p className="text-sm leading-7 text-slate-600 font-poppins">
                  The right feature set turns the platform into a reliable operating layer instead of another disconnected tool.
                </p>
              </div>
              <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-2">
                {features.map((item) => (
                  <article
                    key={item.title}
                    className="group flex h-full flex-col justify-between rounded-[20px] border border-slate-200 bg-white p-4 shadow-[0_16px_45px_rgba(0,0,0,0.08)] transition duration-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(0,0,0,0.12)]"
                  >
                    <div className="space-y-2">
                      <h3 className="text-[18px] font-semibold text-slate-900 font-unbounded leading-tight">
                        {item.title}
                      </h3>
                      <p className="text-sm leading-7 text-slate-600 font-poppins">
                        {item.description}
                      </p>
                    </div>
                  </article>
                ))}
              </div>
            </div>

            <div className="relative">
              <img
                src={coreFeaturesImg}
                alt="Core features illustration"
                className="w-full h-auto rounded-[32px]"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="pb-16 md:pb-20 relative">
        <div className="mx-auto max-w-7xl px-5 lg:px-16">
          <div className="grid gap-10 lg:grid-cols-[minmax(0,1.1fr)_minmax(360px,0.9fr)] lg:items-center">
            <div className="relative">
              <img
                src={buildVsBuyImg}
                alt="Build vs buy illustration"
                className="w-full h-auto rounded-[32px]"
                loading="lazy"
                decoding="async"
              />
            </div>

            <div className="space-y-8">
              <div className="space-y-3">
                <h3 data-aos="fade-up" className="font-peachi text-[28px] font-bold text-black mb-2 lg:mb-8 aos-init aos-animate">
                  <span className="text-primary"> Build vs Buy </span>
                </h3>
                <h2 className="text-3xl font-semibold leading-tight text-slate-900 md:text-4xl font-unbounded">
                  Why custom development is often the better long-term choice
                </h2>
                <p className="text-sm leading-7 text-slate-600 font-poppins">
                  Off-the-shelf software is convenient when your process is standard. Custom development becomes more valuable when your workflow, reporting, or integration needs are more specific.
                </p>
                <p className="text-sm leading-7 text-slate-600 font-poppins">
                  If the goal is cleaner approvals, reporting, and connected workflows, pair the platform with <Link to="/services/ai-automation" className="text-primary underline hover:text-secondary">AI automation services</Link> or review our guide to <Link to="/blog/how-to-build-a-smarter-operations-stack-for-a-growing-brand" className="text-primary underline hover:text-secondary">building a smarter operations stack</Link>.
                </p>
              </div>
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-2">
                {buildVsBuy.map((column) => (
                  <article
                    key={column.title}
                    className="group flex h-full flex-col justify-between rounded-[20px] border border-slate-200 bg-white p-4 shadow-[0_16px_45px_rgba(0,0,0,0.08)] transition duration-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(0,0,0,0.12)]"
                  >
                    <div className="space-y-2">
                      <h3 className="text-[18px] font-semibold text-slate-900 font-unbounded leading-tight">
                        {column.title}
                      </h3>
                      <ul className="space-y-3 text-sm leading-7 text-slate-600 font-poppins">
                        {column.points.map((point) => (
                          <li key={point} className="flex gap-3">
                            <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-[#FF6B00]" />
                            <span>{point}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-16 md:pb-20 relative">
        <div className="mx-auto max-w-7xl px-5 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] gap-10 lg:items-start pt-10">
            <div className="lg:sticky lg:top-32 space-y-8">
              <div className="space-y-4">
                <h3 data-aos="fade-up" className="font-peachi text-[28px] font-bold text-black mb-4 lg:mb-8 aos-init aos-animate">
                  <span className="text-primary"> Development flow </span>
                </h3>
                <h2 className="text-3xl font-semibold leading-tight text-slate-900 md:text-4xl font-unbounded">
                  How we approach SaaS and ERP delivery
                </h2>
                <p className="text-base leading-8 text-slate-600 font-poppins">
                  The process stays aligned to business logic, permission structure, integrations, and launch planning from the beginning.
                </p>
              </div>
              <div className="mt-4">
                <img
                  src={developmentFlowImg}
                  alt="Development flow illustration"
                  className="w-full h-auto rounded-[24px] lg:rounded-[32px] shadow-[0_12px_40px_rgba(0,0,0,0.06)]"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6">
              {processSteps.map((step, index) => (
                <article
                  key={step.title}
                  data-aos="fade-up"
                  data-aos-delay={index * 100}
                  className="rounded-[24px] lg:rounded-[32px] border border-slate-100 bg-white p-6 lg:p-8 flex flex-col justify-between shadow-[0_12px_40px_rgba(0,0,0,0.06)] hover:shadow-[0_18px_50px_rgba(0,0,0,0.1)] transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="space-y-6">
                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#FFF3E9] text-sm font-semibold text-[#FF6B00]">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                      {step.label || "Step"}
                    </p>
                    <h3 className="text-xl font-bold text-slate-900 font-unbounded leading-tight">
                      {step.title}
                    </h3>
                    <p className="text-sm leading-7 text-slate-600 font-poppins">
                      {step.description}
                    </p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <SectionWrap
        eyebrow="FAQ"
        title="Common questions about SaaS and ERP development"
        description="These answers help teams think through scope, architecture, and return on investment before they start."
        image={saasErpFaqImg}
        imageAlt="Frequently asked questions about SaaS and ERP development"
        imagePosition="right"
      >
        <FaqList faqs={faqs} />
      </SectionWrap>

      <SectionWrap
        eyebrow="CTA"
        title="Build a platform that fits your operations"
        description="If your business has outgrown templates, spreadsheets, or disconnected tools, we can help shape a platform that actually matches the way you work."
        suggestion={{
          title: "Suggested CTA image",
          text: "Use a leadership meeting scene with a platform roadmap, product wireframes, and dashboards on a bright desk with orange accents."
        }}
        rightContent={
          <div data-aos="fade-left">
            <ServiceInquiryForm
              title="Book a free SaaS or ERP consultation"
              description="Tell us the process, product, or system you want to build. We will respond with the best next step."
              submitLabel="Book a Free Consultation"
              serviceName="SaaS & ERP Development"
              defaultSource="SaaS & ERP Development page"
              sourceOptions={[
                "SaaS & ERP Development page",
                "Referral",
                "Search",
                "Social media",
                "Other",
              ]}
              successTitle="Your platform enquiry is in"
              successDescription="Thanks for the details. We will review your goals and get back to you with a practical response."
            />
          </div>
        }
      >
        <div className="space-y-6" id="service-inquiry">
          <p className="text-sm leading-7 text-slate-600 font-poppins">
            If you are still deciding where to start, compare platform work with <Link to="/blog/how-to-choose-the-right-ai-automation-workflow-for-your-business" className="text-primary underline hover:text-secondary">the right automation workflow</Link> or explore supporting <Link to="/services/web-mobile-development" className="text-primary underline hover:text-secondary">web and mobile development</Link>.
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            {[
              "Own the logic, not just the interface",
              "Centralize approvals, reporting, and data",
              "Integrate with the tools you already use",
              "Plan a platform that can scale with demand",
            ].map((item, index) => (
              <div
                key={item}
                data-aos="fade-up"
                data-aos-delay={index * 100}
                className="rounded-[10px] border border-slate-200 bg-[#FFF9F4] px-4 py-3 text-[15px] font-light text-lightBlack line-clamp-3 transition-all duration-300 hover:border-primary hover:shadow-md"
              >
                {item}
              </div>
            ))}
          </div>
        </div>
      </SectionWrap>

      <RelatedServices links={relatedLinks} variant="service" />
    </ServicePageShell>
  );
};

export default SaasErpDevelopment;
